import { shallowCopy, softRandomUUID } from "./Utils";
import JSZip from "jszip";

// Cache buster: 2025-11-06-02:00 - Dynamic EC2 IP from API Gateway health check

class Storage {
  constructor() { this._storage = {}; }
  getItem(key) { return this._storage[key]; }
  setItem(key, value) { this._storage[key] = value; }
  removeItem(key) { delete this._storage[key]; }
  clear() { this._storage = {}; }
  key(index) { return Object.keys(this._storage)[index]; }
  length() { return Object.keys(this._storage).length; }
}

const sessionStorage = new Storage();

// In-memory cache for EC2 endpoint (refreshed on every health check)
let cachedEC2Endpoint = null;


let sessionLostCallback = null;
let lastLoginTime = 0;

const parseJson = async (response) => {
  const t = await response.json();
  if (t.new_session_token) {
    sessionStorage.setItem('snorkle_auth_token', t.new_session_token);
    lastLoginTime = Date.now();
    return t.data;
  }
  return t;
}

export const setSessionLostCallback = (callback) => {
  sessionLostCallback = callback;
};

const handleSessionLost = () => {
  // Prevent session lost callback from triggering immediately after login
  const timeSinceLogin = Date.now() - lastLoginTime;
  if (timeSinceLogin < 5000) { // 5 seconds grace period after login
    return;
  }

  if (sessionLostCallback) {
    sessionLostCallback();
  }
};

// Enhanced fetch wrapper for mobile Safari compatibility
const safeFetch = (url, options = {}, bypassSessionCheck = false) => {
  // Intentionally not used for now; kept for potential future UA handling
  // const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent);
  const timeSinceLogin = Date.now() - lastLoginTime;

  // Simple fetch options with proper credentials handling
  const fetchOptions = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  // Add the auth token to the headers if it exists in sessionStorage
  const token = sessionStorage.getItem('snorkle_auth_token');
  if (token) {
    fetchOptions.headers['X-Session-Token'] = token;
  }

  return fetch(url, fetchOptions)
    .then(async (response) => {
      const token = response.headers.get("x-session-token") || response.headers.get("X-Session-Token");
      if (token) {
        sessionStorage.setItem("snorkle_auth_token", token);
      }
      // Only handle session lost on actual 401 responses, but be less aggressive
      if (response.status === 401 && timeSinceLogin > 10000 && !bypassSessionCheck) { // Only after 10 seconds
        handleSessionLost();
      } else if (response.status === 401) {
        // Not triggering session lost within grace period
      }
      return response;
    })
    .catch((error) => {
      throw error;
    });
};

const APIEndpoint = process.env.REACT_APP_API_ENDPOINT;

const signalQueryStart = (id, title) => {
  window.postMessage({ description: "APIQueryStart", id: id, title: title });
}
const signalQuerySuccess = (id, url) => {
  window.postMessage({ description: "APIQuerySuccess", id: id, url: url });
}
const signalQueryFailure = (id, msg) => {
  window.postMessage({ description: "APIQueryFailure", id: id, message: msg });
}

const createUid = () =>
  String(
    Date.now().toString(32) +
    Math.random().toString(16)
  ).replace(/\./g, '')

export async function APILogin(email, pass, successCB, failureCB) {
  const url = APIEndpoint + "/login";

  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email: email, password: pass }),
  })
    .then(async (response) => {
      const body = await parseJson(response);

      if (response.ok) {


        successCB(body);
      } else {
        failureCB(body);
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIRegister(
  email,
  name,
  pass,
  passConfirm,
  useType,
  successCB,
  failureCB
) {
  const url = APIEndpoint + "/register";
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      name: name,
      password: pass,
      password_confirmation: passConfirm,
      use_case: useType,
    }),
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body);
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetUserInfo(successCB, failureCB) {
  const url = APIEndpoint + "/userinfo";
  

  return safeFetch(url, {
    method: "GET",
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        const token = response.headers.get("x-session-token");
        if (token) {
          sessionStorage.setItem("snorkle_auth_token", token);
        }
        
        successCB(body);
      } else {
        failureCB(body);
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIGetVisualizations(successCB, failureCB, visCache) {
  const url = APIEndpoint + "/visualizations";

  safeFetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Supports-Folders": "true",
    },
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        const responseVisCache = {};
        if (body.visuals && Array.isArray(body.visuals)) {
          body.visuals.forEach(vis => {
            responseVisCache[vis.id] = vis;
          });

          body.visuals.forEach(vis => {
            if ((vis.type === 'scrollytelling' || vis.type === 'presentation' || vis.type === 'slideshow') && vis.slides && vis.slides.length > 0) {
              const firstSlide = vis.slides[0];
              if (firstSlide.visual_id) {
                const childVis = responseVisCache[firstSlide.visual_id] || (visCache ? visCache[firstSlide.visual_id] : null);
                if (childVis && childVis.thumbnail) {
                  vis.thumbnail = childVis.thumbnail;
                }
              }
            }
          });
        }

        // Resolve thumbnails for presentations (scrollytelling stories, quizzes, etc.)
        // that reference a visual via slides[0].visual_id
        if (body.presentations && Array.isArray(body.presentations)) {
          body.presentations.forEach(pres => {
            if (!pres.thumbnail && pres.slides && pres.slides.length > 0) {
              const firstSlide = pres.slides[0];
              if (firstSlide && firstSlide.visual_id) {
                const childVis = responseVisCache[firstSlide.visual_id] || (visCache ? visCache[firstSlide.visual_id] : null);
                if (childVis && childVis.thumbnail) {
                  pres.thumbnail = childVis.thumbnail;
                }
              }
            }
          });
        }

        successCB(body);
      } else {
        failureCB(body);
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIWebVisualization(
  resourceId,
  resourceType,
  name,
  description,
  successCBInit,
  failureCBInit,
  isOrg,
  args
) {
  const id = createUid();
  signalQueryStart(id, `Creating ${name ? '"' + name + '"' : (resourceType.replace('_', ' ').toUpperCase() + " Visual: " + resourceId)}`)
  const successCB = (body) => { signalQuerySuccess(id, body.visualization_url); successCBInit(body) };
  const failureCB = (body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url =
    APIEndpoint + (isOrg ? "/organization/add_visual" : "/visualizations/web");
  let req_header = { "Content-Type": "application/json" };
  return safeFetch(url, {
    method: "POST",
    headers: req_header,
    credentials: "include",
    body: JSON.stringify(
      Object.assign(args || {}, {
        resource_id: resourceId || "",
        resource_type: resourceType,
        title: name,
        description: description,
      })
    ),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

const pollForUploadedFile = (
  id,
  successCB,
  failureCB,
  waitSec,
  statusFunc,
  errorFunc = () => "Internal Server Error",
  repollInterval = 15
) => {
  const url = APIEndpoint + "/visualizations/" + id;
  setTimeout(() => {
    safeFetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        const body = await parseJson(response);
        if (!response.ok) {
          failureCB(body);
        } else {
          const res = statusFunc(body);
          if (res === "error") {
            const res = errorFunc(body);
            failureCB(res);
          } else if (res === "complete") {
            successCB(body["visualization"]);
          } else {
            pollForUploadedFile(
              id,
              successCB,
              failureCB,
              repollInterval,
              statusFunc,
              errorFunc,
              repollInterval
            );
          }
        }
      })
      .catch((reason) => {
        failureCB(reason);
      });
  }, waitSec * 1000);
};

const uploadFileToS3 = (
  id,
  postUrl,
  fields,
  file,
  successCB,
  failureCB,
  statusFunc,
  errorFunc
) => {
  const formData = new FormData();
  Object.keys(fields).forEach((key) => {
    formData.append(key, fields[key]);
  });
  formData.append("file", file);
  return fetch(postUrl, {
    method: "POST",
    body: formData,
  })
    .then(async (response) => {
      if (response.ok) {
        pollForUploadedFile(id, successCB, failureCB, 3, statusFunc, errorFunc);
        return true;
      } else {
        throw new Error("File upload failed");
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
};

export function APIFileVisualization(
  file,
  name,
  description,
  successCBInit,
  failureCBInit,
  isOrg,
  args = {}
) {
  const id = createUid();
  const dispName = name || (((args.filename || "").split('.')[0] || file.name || "FILE") + " Visual");
  signalQueryStart(id, `Creating ${dispName}`)
  const successCB = (body) => { signalQuerySuccess(id, body.visualization_url); successCBInit(body) };
  const failureCB = (body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url =
    APIEndpoint +
    (isOrg ? "/organization/add_visual" : "/visualizations/files");
  let req_header = { "Content-Type": "application/json" };
  return safeFetch(url, {
    method: "POST",
    headers: req_header,
    credentials: "include",
    body: JSON.stringify(
      Object.assign(
        {
          filename: file.name || (typeof file === "string" ? file : "file"),
          title: name,
          description: description,
          resource_type: "file",
        },
        args
      )
    ),
  })
    .then(async (response) => {
      const body = await parseJson(response);

      const vis_id = body["id"];
      const upload_url = body["url"] || null;
      if (response.ok) {
        if (upload_url) {
          uploadFileToS3(
            vis_id,
            upload_url,
            body["fields"],
            file,
            successCB,
            failureCB,
            (body) => body.status,
            (body) => body.message
          );
        } else {
          pollForUploadedFile(vis_id, successCB, failureCB, 3, (body) => body.status,
            (body) => body.message);
        }
      } else {
        failureCB(body);
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APILogout() {
  const url = APIEndpoint + "/login";
  // Clear session fallback and auth token on logout
  sessionStorage.removeItem('snorkle_auth_token');

  return safeFetch(url, {
    method: "DELETE",
    body: "",
  });
}

export async function APIDeleteVisualizations(
  vis,
  successCBInit,
  failureCBInit,
  isOrg
) {
  // const id = createUid();
  // signalQueryStart(id, `Deleting ${vis.title ? '"' + vis.title + '"' : " Visual"}`)
  const successCB = successCBInit//(body) => { signalQuerySuccess(id, ""); successCBInit(body) };
  const failureCB = failureCBInit//(body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url =
    APIEndpoint +
    (isOrg ? "/organization/remove_visual/" : "/visualizations/") +
    vis.id;
  return safeFetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: "",
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIResetPassword(
  newPassword,
  newPasswordConfirm,
  successCB,
  failureCB,
  resetToken,
  currentPass
) {
  const url = APIEndpoint + "/resetpass";
  let msgBody = {
    new_password: newPassword,
    new_password_confirm: newPasswordConfirm,
  };
  if (resetToken) {
    msgBody["token"] = resetToken;
  }
  if (currentPass) {
    msgBody["current_pass"] = currentPass;
  }
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(msgBody),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIRequestPasswordReset(email, successCB, failureCB) {
  const url = APIEndpoint + "/requestpasswordreset";
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email: email }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIEditVisualizations(
  oldVis,
  newTitle,
  newDesc,
  newThumbnail,
  newShowShare,
  successCBInit,
  failureCBInit,
  isOrg,
  other = {}
) {
  //   const id = createUid();
  //   signalQueryStart(id, `Editing "${newTitle || oldVis.title}"`)
  const successCB = successCBInit; //(body) => { signalQuerySuccess(id, ""); successCBInit(body) };
  const failureCB = failureCBInit;//(body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url =
    APIEndpoint +
    (isOrg ? "/organization/edit_visual/" : "/visualizations/") +
    oldVis.id;
  let msg_body = { newTitle: newTitle, newDescription: newDesc };
  if (newThumbnail) {
    msg_body.newThumbnail = newThumbnail;
  }
  if (newShowShare !== null) {
    msg_body.showShareDialog = newShowShare;
  }
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(Object.assign(other, msg_body)),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB("Internal Server Error.");
    });
}

export async function APIEditDefaultView(
  vis,
  newView,
  successCB,
  failureCB,
  isOrg
) {
  const url =
    APIEndpoint +
    (isOrg ? "/organization/edit_visual/" : "/visualizations/") +
    vis.id;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ savedView: newView }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB("Internal Server Error.");
    });
}

export async function APIGetOrgVisuals(successCB, failureCB) {
  const url = APIEndpoint + "/organization/get_visuals";
  return safeFetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "X-Supports-Folders": "true",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetOrgInfo(successCB, failureCB) {
  const url = APIEndpoint + "/organization/get_info";
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIInviteOrgMember(
  email,
  permissions,
  successCB,
  failureCB
) {
  const url = APIEndpoint + "/organization/add_member";
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      permissions: permissions,
    }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIRemoveMember(email, successCB, failureCB) {
  const url = APIEndpoint + "/organization/remove_member";
  return safeFetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email: email }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIConfirmOrgJoin(token, successCB, failureCB) {
  const url = APIEndpoint + "/organization/confirm_join";
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ token: token }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIEditMember(email, permissions, successCB, failureCB) {
  const url = APIEndpoint + "/organization/edit_member";
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email: email,
      permissions: permissions,
    }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APISetBranding(
  name,
  website,
  twitter,
  icon,
  qr,
  favicon,
  profile_name,
  id,
  successCB,
  failureCB,
  isOrg,
  args = {}
) {
  const url =
    APIEndpoint + (isOrg ? "/organization/set_branding" : "/branding");
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      Object.assign(args, {
        name: name,
        website: website,
        twitter: twitter,
        icon: icon,
        qr: qr,
        favicon: favicon,
        profile_name: profile_name,
        id: id,
      })
    ),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIRemoveBranding(id, successCB, failureCB, isOrg) {
  const url =
    APIEndpoint + (isOrg ? "/organization/set_branding" : "/branding");
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id: id, remove: true }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetBranding(successCB, failureCB, isOrg) {
  const url =
    APIEndpoint + (isOrg ? "/organization/get_branding" : "/branding");
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APISetVisThumbnail(
  vis,
  newThumbnail,
  successCBInit,
  failureCBInit,
  isOrg
) {
  const id = createUid();
  signalQueryStart(id, `Set image for "${vis.title}"`);
  const successCB = (body) => { signalQuerySuccess(id, ""); successCBInit(body) };
  const failureCB = (body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url =
    APIEndpoint +
    (isOrg ? "/organization/edit_visual/" : "/visualizations/") +
    vis.id;
  let msg_body = { newThumbnail: newThumbnail };
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(msg_body),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB("Internal Server Error.");
    });
}

export async function APIGetTags(successCB, failureCB) {
  const url = APIEndpoint + "/tags";
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetTaggedVisuals(slug, successCB, failureCB) {
  const url = APIEndpoint + "/tags/" + slug;
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIAddTags(newTags, successCB, failureCB) {
  const url = APIEndpoint + "/tags";
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ tags: newTags }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APISearchVisuals(term, successCB, failureCB) {
  const url = APIEndpoint + "/search?q=" + encodeURIComponent(term);
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);
      if (response.ok) {
        cbFunc(body);
      } else {
        failureCB(body);
      }
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetDeletedVisuals(successCB, failureCB) {
  const url = APIEndpoint + "/visualizations/deleted";
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIRestoreDeletedVisual(vis, successCBInit, failureCBInit) {
  const url = `${APIEndpoint}/visualizations/${vis.id}/restore`;
  // const id = createUid();
  // signalQueryStart(id, `Restoring "${vis.title}"`);
  const successCB = successCBInit// (body) => { signalQuerySuccess(id, ""); successCBInit(body) };
  const failureCB = failureCBInit// (body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIRequestSubscriptionModification(
  newTier,
  monthly,
  successCB,
  failureCB,
  password = "",
  other = {}
) {
  const url = `${APIEndpoint}/subscriptions/modify`;
  const body = shallowCopy(other);
  body.newTier = newTier;
  body.monthly = monthly;
  if (password) {
    body.password = password;
  }
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetBillingDetails(
  successCB,
  failureCB,
  password = ""
) {
  const url = `${APIEndpoint}/billing`;
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Confirmation": password || "",
    },
  }, true)
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body, response.status);
    })
    .catch((reason) => {
      failureCB(reason, null);
    });
}

const DownloadS3File = (url, scb, fcb) => {
  fetch(url, {
    method: "GET",
  })
    .then(async (response) => {
      scb(await response.text());
    })
    .catch((reason) => fcb(reason));
};

export function APIGetDownloadableVersion(
  vis,
  successCBInit,
  failureCBInit,
  isOrg
) {
  const uid = createUid();
  signalQueryStart(uid, `Downloading "${vis.title}"`);
  const successCB = (body) => { signalQuerySuccess(uid, ""); successCBInit(body) };
  const failureCB = (body) => { signalQueryFailure(uid, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const core = (vis,
    successCB,
    failureCB,
    isOrg) => {
    const url =
      APIEndpoint +
      (isOrg
        ? `/organization/download_visual/${vis.id}`
        : `/visualizations/${vis.id}/download`);
    safeFetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        const body = await parseJson(response);
        if (!response.ok) {
          failureCB(body);
        } else if (response.status === 200) {
          const downloadableUrl = body["url"];
          DownloadS3File(downloadableUrl, successCB, failureCB);
        } else if (response.status === 201) {
          setTimeout(
            core,
            5000,
            vis,
            successCB,
            failureCB,
            isOrg
          );
        } else {
          failureCB("Internal Server Error. Please try again later.");
        }
      })
      .catch((reason) => failureCB(reason));
  }
  core(vis, successCB, failureCB, isOrg);
}

export async function APICreateVisFolder(successCB, failureCB, isOrg) {
  const url = APIEndpoint + (isOrg ? "/organization/folders" : "/folders");
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(null),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIEditVisualFolder(
  id,
  args,
  successCB,
  failureCB,
  isOrg
) {
  const url = `${APIEndpoint}/${isOrg ? "organization/folders" : "folders"
    }/${id}`;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(args),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIDeleteVisualFolder(folder, successCB, failureCB, isOrg) {
  const url = `${APIEndpoint}/${isOrg ? "organization/folders" : "folders"
    }/${folder.id}`;
  return safeFetch(url, {
    method: "DELETE",
    credentials: "include",
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIAddEditPresentation(
  pres,
  successCBInit,
  failureCBInit,
  isOrg,
  type
) {
  // const uid = createUid();
  // signalQueryStart(uid, `${!pres.id ? "Creating" : "Editing"} "${pres.title}"`);
  const successCB = successCBInit //(body) => { signalQuerySuccess(uid, ""); successCBInit(body) };
  const failureCB = failureCBInit //(body) => { signalQueryFailure(uid, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const hasId = !!pres.id;
  let url = `${APIEndpoint}${isOrg ? "/organization" : ""}/presentation`;
  url += hasId ? "/" + pres.id : "";
  pres.type = type;
  return safeFetch(url, {
    method: hasId ? "PUT" : "POST",
    credentials: "include",
    body: JSON.stringify(pres),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIDeletePresentation(pres, successCBInit, failureCBInit, isOrg) {
  const id = createUid();
  const title = `"${pres.title}"`
  const successCB = (body) => { signalQuerySuccess(id, ""); successCBInit(body) };
  const failureCB = (body) => { signalQueryFailure(id, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  signalQueryStart(id, `Deleting presentation ${title}.`)
  const url = APIEndpoint + (isOrg ? "/organization" : "") + "/presentation/" + pres.id;
  return safeFetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    }
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIEditPresentations(presId, args, isOrg) {
  const url = APIEndpoint + (isOrg ? "/organization" : "") + "/presentation/" + presId;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(args)
  });
}

export async function APIEditVisLandmarks(
  vis,
  newLandmarks,
  successCBInit,
  failureCBInit,
  isOrg
) {
  // const uid = createUid();
  // signalQueryStart(uid, `Editing Landmarks for "${vis.title}"`);
  const successCB = successCBInit //(body) => { signalQuerySuccess(uid, ""); successCBInit(body) };
  const failureCB = failureCBInit//(body) => { signalQueryFailure(uid, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url =
    APIEndpoint +
    (isOrg ? "/organization/edit_visual/" : "/visualizations/") +
    vis.id;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ newLandmarks: JSON.stringify(newLandmarks) }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB("Internal Server Error.");
    });
}

export async function APIAddEditBundle(bundle, successCB, failureCB) {
  const hasId = !!bundle.id;
  let url = `${APIEndpoint}/admin/bundle`;
  url += hasId ? "/" + bundle.id : "";
  return safeFetch(url, {
    method: hasId ? "PUT" : "POST",
    credentials: "include",
    body: JSON.stringify(bundle),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetBundles(successCB, failureCB) {
  const url = APIEndpoint + "/bundles";
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIDeleteBundle(id, successCB, failureCB) {
  const url = `${APIEndpoint}/admin/bundle/${id}`;
  return safeFetch(url, {
    method: "DELETE",
    credentials: "include",
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIGetBundlePurchasePI(
  ids,
  billingPeriod,
  successCB,
  failureCB
) {
  const url = `${APIEndpoint}/bundles/purchase`;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    body: JSON.stringify({ bundles: ids, period: billingPeriod }),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export async function APIUpdateUserInfo(info, successCB, failureCB) {
  const url = `${APIEndpoint}/userinfo`;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    body: JSON.stringify(info),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);

      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIContact(info, successCB, failureCB) {
  const url = `${APIEndpoint}/contact`;
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(info),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);
      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIShareEmail(id, isPresentation, email, successCB, failureCB) {
  const url = `${APIEndpoint}/share`;
  const b = {
    id: id,
    isPresentation: isPresentation,
    destinationEmail: email,
  };
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(b),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);
      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIEditAudio(
  vis,
  data,
  autoplay,
  successCBInit,
  failureCBInit,
  args = null
) {
  // const uid = createUid();
  // signalQueryStart(uid, `Editing audio for "${vis.title}"`);
  const successCB = successCBInit//(body) => { signalQuerySuccess(uid, ""); successCBInit(body) };
  const failureCB = failureCBInit//(body) => { signalQueryFailure(uid, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  const url = `${APIEndpoint}/visualizations/${vis.id}/audio`;
  let body = args || {};
  Object.assign(body, {
    audio_data: data,
    autoplay: autoplay,
  });
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      const cbFunc = response.ok ? successCB : failureCB;
      const body = await parseJson(response);
      cbFunc(body);
    })
    .catch((reason) => {
      failureCB(reason);
    });
}

export function APIEditLandmarksWithFiles(
  vis,
  newLandmarks,
  successCBInit,
  failureCBInit,
  isOrg,
  args = {}
) {
  // const uid = createUid();
  // signalQueryStart(uid, `Edit Landmarks for "${vis.title}"`);
  const successCB = successCBInit// (body) => { signalQuerySuccess(uid, ""); successCBInit(body) };
  const failureCB = failureCBInit// (body) => { signalQueryFailure(uid, body.error ? (Array.isArray(body.error) ? body.error[0] : body.error) : body.message || body || "There was a server error. Please try again later."); failureCBInit(body) }
  let audioFiles = [];
  for (const lm of newLandmarks) {
    if (lm.audioFile && !args.voice_d) {
      const p = {
        rename:
          (Math.random() * 64).toString(36).replace(".", "").replace(",", "") +
          ".mp3",
        file: lm.audioFile,
      };
      audioFiles.push(p);
      delete lm.audioFile;
      lm.audioRename = p.rename;
    }
    else if (lm.audioFile === null) {
      delete lm.audioFile;
    }
  }
  let zip = JSZip();
  for (const audioFile of audioFiles) {
    zip.file(audioFile.rename, audioFile.file);
  }
  zip
    .generateAsync({ type: "blob" })
    .then((blob) => {
      const url = `${APIEndpoint}/visualizations/${vis.id}/landmarkaudio`;
      let body = args || {};
      Object.assign(body, {
        landmarks: newLandmarks,
        ...args,
      });
      return safeFetch(url, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify(body),
      })
        .then(async (response) => {
          if (response.ok) {
            const body = await parseJson(response);
            const url = body.url;
            const fields = body.fields;
            const updateId = body.update_id;
            if (url){
            uploadFileToS3(
              vis.id,
              url,
              fields,
              blob,
              successCB,
              failureCB,
              (body) => {
                if (body.visualization.last_update_id === updateId) {
                  return "complete";
                }
                if (body.visualization.last_update_error === updateId) {
                  return "error";
                }
              },
              (body) => body.visualization.update_error
            );
          }
          else {
            pollForUploadedFile(vis.id, successCB, failureCB, 5,  (body) => {
              if (body.visualization.last_update_id === updateId) {
                return "complete";
              }
              if (body.visualization.last_update_error === updateId) {
                return "error";
              }
            },
            (body) => body.visualization.update_error);
            }
          }
          else {
            const body = await parseJson(response);
            failureCB(body);
            return;
          }
        })
        .catch((reason) => {
          failureCB(reason);
        });
    })
    .catch((err) => failureCB(err));
}

export function APICreateStoryFromPDF(file, language, successCB, failureCB, progressCB) {
  const uid = createUid();
  signalQueryStart(uid, `Generating story from ${file.name}`);
  progressCB('preparing');

  const handleError = (error) => {
    const errorMessage = error.message || (typeof error === 'string' ? error : "An unknown error occurred.");
    signalQueryFailure(uid, errorMessage);
    failureCB(errorMessage);
  };

  const pollForStory = (jobId) => {
    const pollUrl = `${APIEndpoint}/scrollytelling/status/${jobId}`;
    setTimeout(() => {
      safeFetch(pollUrl, { method: "GET" })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Polling failed with status: ${response.status}`);
          }
          return parseJson(response);
        })
        .then(data => {
          if (data.status === 'COMPLETED') {
            signalQuerySuccess(uid, "Story generated!");
            successCB(data.storyData);
          } else if (data.status === 'FAILED') {
            throw new Error(data.errorMessage || "Processing failed.");
          } else {
            pollForStory(jobId);
          }
        })
        .catch(handleError);
    }, 5000);
  };

  const startUploadUrl = `${APIEndpoint}/scrollytelling/create_from_pdf`;
  
  safeFetch(startUploadUrl, {
    method: "POST",
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ language: language, fileName: file.name }) // Include fileName if needed by the lambda
  })
  .then(response => {
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status} when requesting upload URL.`);
    }
    return parseJson(response);
  })
  .then(data => {
    const { uploadData, jobId } = data;
    progressCB('uploading');

    // Create a FormData object to send to S3
    const formData = new FormData();
    // Append all the fields that the presigned POST requires
    Object.keys(uploadData.fields).forEach(key => {
      formData.append(key, uploadData.fields[key]);
    });
    // Append the actual file as the last field
    formData.append('file', file);

    // Use a direct fetch POST for the S3 upload, without any custom headers
    return fetch(uploadData.url, {
      method: 'POST',
      body: formData,
    }).then(async response => {
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`File upload to S3 failed.`);
      }
      return jobId;
    });
  })
  .then(jobId => {
    progressCB('processing');
    pollForStory(jobId);
  })
  .catch(handleError);
}

// Assets (non-library) helpers
// Obtain a presigned POST for uploading a non-library asset to S3 based on assetType
export function APIAssetPresign(assetType, filename, contentType, successCB, failureCB, extra = {}) {
  const url = `${APIEndpoint}/assets/presign`;
  const body = Object.assign({ assetType, filename, contentType }, extra);
  try {
    // Log the intent but avoid logging secrets later
  } catch (e) {}
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      const parsed = await parseJson(response);
      const cb = response.ok ? successCB : failureCB;
      try {
        const uploadData = parsed?.uploadData || parsed;
        const host = uploadData?.url ? (new URL(uploadData.url)).host : null;
        const key = uploadData?.fields?.key || null;
        const signedContentType = uploadData?.fields?.['Content-Type'];
      } catch (e) {}
      cb(parsed);
    })
    .catch((reason) => failureCB(reason));
}

// Upload a file to the assets bucket/prefix. Returns final URL on success.
// progressCB(optional): 'preparing' | 'uploading' | 'done'
export function APIUploadAsset(file, assetType, successCB, failureCB, progressCB = null, extra = {}) {
  progressCB && progressCB('preparing');
  return APIAssetPresign(assetType, file.name || 'file', file.type || 'application/octet-stream', (data) => {
    // Support either { uploadData: { url, fields, finalUrl? }, ... } or { url, fields, finalUrl }
    const uploadData = data.uploadData || data;
    const postUrl = uploadData.url;
    const fields = uploadData.fields || {};
    if (!postUrl || !fields) {
      failureCB({ message: 'Invalid presign response' });
      return;
    }

    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append('file', file);

    progressCB && progressCB('uploading');
    try {
      const host = (new URL(postUrl)).host;
    } catch (e) {}
    fetch(postUrl, {
      method: 'POST',
      body: formData,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          try {
          } catch (e) {}
          failureCB({ message: 'File upload to S3 failed', status: response.status, error: errorText });
          return;
        }

        // Determine final URL: prefer explicit finalUrl, otherwise derive from url + key
        let finalUrl = uploadData.finalUrl || data.finalUrl;
        if (!finalUrl && fields.key && typeof postUrl === 'string') {
          const base = postUrl.endsWith('/') ? postUrl.slice(0, -1) : postUrl;
          finalUrl = `${base}/${fields.key}`;
        }
        progressCB && progressCB('done');
        try {
        } catch (e) {}
        successCB({ finalUrl, key: fields.key || null });
      })
      .catch((reason) => failureCB(reason));
  }, failureCB, extra);
}

// Promote an asset from staging to a final location (e.g., story figure selection)
export function APIAssetPromote(sourceUrl, assetType, context = {}, successCB, failureCB) {
  const url = `${APIEndpoint}/assets/promote`;
  const body = Object.assign({ sourceUrl, assetType }, context);
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      const parsed = await parseJson(response);
      const cb = response.ok ? successCB : failureCB;
      cb(parsed);
    })
    .catch((reason) => failureCB(reason));
}

export function APIGenerateExplainer(args, successCB, failureCB, progressCB = null) {
  const url = `${APIEndpoint}/explainer`;
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(args),
  })
    .then(async (response) => {
      const parsed = await parseJson(response);
      if(!response.ok || parsed.status === "error") {
        failureCB(parsed.error);
        return;
      }
      if (parsed.status === "success") {
        successCB(parsed);
        return;
      }
      if (parsed.status === "working") {
        progressCB?.(parsed);
      }
      const id = parsed.id;
      setTimeout(() => {
        APIGenerateExplainer({id: id}, successCB, failureCB, progressCB);
      }, 2000);
    })
    .catch((reason) => failureCB(reason));
}

// ============================================================================
// ORGAN SEGMENTATION API
// ============================================================================
// These functions interface with the organ segmentation service via API Gateway.
// Note: These do NOT use session authentication (separate service).

// Resolve Organ Segmentation API base endpoint with sane defaults and normalization
const resolveOrganSegBase = () => {
  const devDefault = 'https://api.snorkle.io/dev';
  try {
    if (typeof window !== 'undefined' && /localhost/i.test(window.location.host)) {
      return devDefault;
    }
  } catch (_) {}

  const fallback = devDefault;
  const raw = (process.env.REACT_APP_ORGAN_SEG_API_ENDPOINT || fallback).trim();
  try {
    const u = new URL(raw);
    // Normalize pathname by removing trailing slashes and legacy segments
    const segments = u.pathname.split('/').filter(Boolean);
    const filtered = segments.filter(seg => seg !== 'organ-segmentation' && seg !== 'prod');
    u.pathname = '/' + filtered.join('/');
    // Avoid a base that ends with '/api' (we append '/api/...')
    if (u.pathname.endsWith('/api')) {
      u.pathname = u.pathname.slice(0, -4);
    }
    const normalized = u.toString().replace(/\/+$/, '');
    return normalized;
  } catch {
    return fallback;
  }
};

export const OrganSegAPIEndpoint = resolveOrganSegBase();

// Direct EC2 endpoint for file uploads (Lambda doesn't handle multipart/form-data well)
// This is temporary data that gets deleted after processing, so direct access is acceptable
// Updated: 2025-11-06 - New IP after instance cycle
const OrganSegEC2Endpoint = process.env.REACT_APP_ORGAN_SEG_EC2_ENDPOINT || 
  'http://98.92.86.8:8080';

/**
 * Get the current EC2 endpoint (uses cached value from health check if available)
 * @returns {string} The EC2 endpoint URL
 */
export function getOrganSegEC2Endpoint() {
  return cachedEC2Endpoint || OrganSegEC2Endpoint;
}

/**
 * Upload a medical imaging file (NIfTI, DICOM, or zipped DICOM)
 * Note: Uses direct EC2 endpoint to avoid Lambda multipart/form-data issues
 * @param {File} file - The medical imaging file to upload
 * @param {string} modality - "ct" or "mri"
 * @param {function} successCB - Called with response data on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegUploadFile(file, modality, successCB, failureCB) {
  
  // Step 1: Get presigned S3 URL from Lambda
  const presignedUrl = `${OrganSegAPIEndpoint}/api/upload/presigned`; // MOVED TO /api/
  
  
  fetch(presignedUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      filename: file.name,
      modality: modality,
    }),
  })
    .then(async (response) => {
      const body = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        failureCB(body.error || body.message || 'Failed to get upload URL - backend may not support S3 uploads');
        return;
      }
      
      
      // Step 2: Upload directly to S3 using presigned POST
      const formData = new FormData();
      
      // Add all the fields from the presigned POST
      Object.keys(body.fields).forEach(key => {
        formData.append(key, body.fields[key]);
      });
      
      // Add the file last (required by S3)
      formData.append('file', file);
      
      
      return fetch(body.url, {
        method: 'POST',
        body: formData,
        // No credentials needed for presigned S3 URL
      })
        .then(async (s3Response) => {
          if (!s3Response.ok) {
            const s3Error = await s3Response.text();
            failureCB('Failed to upload file to storage');
            return;
          }
          
          
          // Step 3: Confirm upload with EC2 backend
          // THIS IS THE FIX: The confirmation endpoint MUST be under /api for CORS.
          const confirmUrl = `${OrganSegAPIEndpoint}/api/upload`;
          
          
          const confirmPayload = {
            upload_id: body.upload_id,
            s3_key: body.s3_key,
            filename: file.name,
            modality: modality,
          };
          
          
          return fetch(confirmUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(confirmPayload),
          });
        })
        .then(async (confirmResponse) => {
          if (!confirmResponse) return; // Already handled error above
          
          const confirmBody = await confirmResponse.json();
          
          if (confirmResponse.ok) {
            successCB(confirmBody);
          } else {
            // Fallback: Some backend versions expect direct multipart to /api/upload
            const needsFileField = confirmResponse.status === 422 ||
              (confirmBody && (confirmBody.detail || confirmBody.message) &&
               JSON.stringify(confirmBody).toLowerCase().includes('file'));
            if (needsFileField) {
              const form = new FormData();
              form.append('file', file);
              // Include modality as a hint if backend accepts it; harmless if ignored
              try { form.append('modality', modality || 'ct'); } catch (_) {}
              const directUrl = `${OrganSegAPIEndpoint}/api/upload/direct`;
              return fetch(directUrl, {
                method: 'POST',
                body: form,
              })
                .then(async (directResp) => {
                  const directBody = await directResp.json().catch(() => ({}));
                  if (directResp.ok) {
                    successCB(directBody);
                  } else {
                    failureCB(directBody.detail || directBody.message || 'Direct upload failed');
                  }
                })
                .catch((err) => {
                  // Final fallback: hit EC2 backend directly (bypass API Gateway CORS)
                  try {
                    // Use cached EC2 endpoint if available, otherwise fallback to hardcoded
                    const ec2Base = cachedEC2Endpoint || OrganSegEC2Endpoint;
                    const ec2Url = `${ec2Base}/api/upload`;
                    return fetch(ec2Url, { method: 'POST', body: form })
                      .then(async (ec2Resp) => {
                        const ec2Body = await ec2Resp.json().catch(() => ({}));
                        if (ec2Resp.ok) {
                          successCB(ec2Body);
                        } else {
                          failureCB(ec2Body.detail || ec2Body.message || 'EC2 direct upload failed');
                        }
                      })
                      .catch((e2) => {
                        failureCB(e2.message || 'Network error during EC2 direct upload');
                      });
                  } catch (finalErr) {
                    failureCB(finalErr.message || 'Direct upload failed');
                  }
                });
            } else {
            failureCB(confirmBody.detail || confirmBody.message || 'Upload confirmation failed');
            }
          }
        });
    })
    .catch((error) => {
      failureCB(error.message || 'Network error during upload');
    });
}

/**
 * Start the EC2 instance (if not already running)
 * Called automatically when user opens the /organ-segmentation page
 * @param {function} successCB - Called with status data on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegStartInstance(successCB, failureCB) {
  const url = `${OrganSegAPIEndpoint}/api/start`; // Changed to conform to the working /api/ pattern
  
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        if (successCB) successCB(body);
      } else {
        if (failureCB) failureCB(body.error || body.message || 'Failed to start instance');
      }
    })
    .catch((error) => {
      if (failureCB) failureCB(error.message || 'Network error starting instance');
    });
}

/**
 * Stop the EC2 instance (terminate the ASG)
 * @param {function} successCB - Called on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegStopInstance(successCB, failureCB) {
  const url = `${OrganSegAPIEndpoint}/api/stop`;
  
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  })
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        if (successCB) successCB(body);
      } else {
        if (failureCB) failureCB(body.error || body.message || 'Failed to stop instance');
      }
    })
    .catch((error) => {
      if (failureCB) failureCB(error.message || 'Network error stopping instance');
    });
}

export function APIOrganSegHealthCheck(successCB, failureCB) {
  const url = `${OrganSegAPIEndpoint}/api/health`;
  
  return fetch(url)
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        // Cache the EC2 endpoint from the health check response (provided by proxy Lambda)
        if (body.ec2_endpoint) {
          cachedEC2Endpoint = body.ec2_endpoint;
        }
        if (successCB) successCB(body);
      } else {
        if (failureCB) failureCB(body.error || body.message || 'Health check failed');
      }
    })
    .catch((error) => {
      if (failureCB) failureCB(error.message || 'Network error during health check');
    });
}

/**
 * Segment one or more organs
 * @param {string[]} organs - Array of organ names to segment (e.g., ["spleen", "liver"])
 * @param {string} modality - "ct" or "mri"
 * @param {boolean} fast - Use fast mode (lower quality, faster)
 * @param {function} successCB - Called with segmentation results on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegSegment(organs, modality, fast, successCB, failureCB) {
  
  // Use EC2 direct for segmentation (API Gateway has CORS issues with this endpoint)
  const ec2Base = cachedEC2Endpoint || OrganSegEC2Endpoint;
  const ec2Url = `${ec2Base}/api/segment`;
  
  return fetch(ec2Url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ organs, modality, fast }),
  })
    .then(async (response) => {
      const body = await response.json();
      
      // Check BOTH HTTP status AND body.success field
      if (response.ok && body.success !== false) {
        successCB(body);
      } else {
        failureCB(body.detail || body.message || 'Segmentation failed');
      }
    })
    .catch((error) => {
      failureCB(error.message || 'Network error during segmentation');
    });
}

/**
 * Get segmentation status (for polling during processing)
 * @param {function} successCB - Called with status data on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegGetStatus(successCB, failureCB) {
  // Use EC2 direct for status polling (API Gateway has CORS issues with this endpoint)
  const ec2Base = cachedEC2Endpoint || OrganSegEC2Endpoint;
  const ec2Url = `${ec2Base}/api/segmentation-status`;
  
  return fetch(ec2Url, { method: 'GET' })
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body.detail || body.message || 'Status check failed');
      }
    })
    .catch((error) => {
      failureCB(error.message || 'Network error during status check');
    });
}

/**
 * Get slice information for an organ
 * @param {string} organ - Organ name or key (e.g., "spleen" or "kidney_right_spleen" for multi-organ)
 * @param {function} successCB - Called with slice data on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegGetSlices(organ, successCB, failureCB) {
  const url = `${OrganSegAPIEndpoint}/api/slices/${encodeURIComponent(organ)}`;
  
  return fetch(url, {
    method: 'GET',
  })
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body.detail || body.message || 'Failed to get slices');
      }
    })
    .catch((error) => {
      failureCB(error.message || 'Network error getting slices');
    });
}

/**
 * Get a specific slice image (base64 encoded)
 * @param {string} organ - Organ name
 * @param {number} sliceIdx - Slice index
 * @param {function} successCB - Called with slice data on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegGetSliceImage(organ, sliceIdx, successCB, failureCB) {
  const url = `${OrganSegAPIEndpoint}/api/slice/${encodeURIComponent(organ)}/${sliceIdx}`;
  
  return fetch(url, {
    method: 'GET',
  })
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body.detail || body.message || 'Failed to get slice image');
      }
    })
    .catch((error) => {
      failureCB(error.message || 'Network error getting slice image');
    });
}

/**
 * Get 3D mesh data for an organ
 * @param {string} organ - Organ name
 * @param {function} successCB - Called with mesh data on success
 * @param {function} failureCB - Called with error on failure
 */
export function APIOrganSegGetMesh(organ, successCB, failureCB) {
  const url = `${OrganSegAPIEndpoint}/api/mesh/${encodeURIComponent(organ)}`;
  
  return fetch(url, {
    method: 'GET',
  })
    .then(async (response) => {
      const body = await response.json();
      
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body.detail || body.message || 'Failed to get mesh data');
      }
    })
    .catch((error) => {
      failureCB(error.message || 'Network error getting mesh');
    });
}

export function APIDeleteExplainer(explainer, successCB, failureCB) {
  const url = `${APIEndpoint}/explainer/${explainer.id}`;
  return safeFetch(url, {
    method: "DELETE",
    credentials: "include",
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        successCB(body);
      } else {
        failureCB('Failed to delete explainer');
      }
    })
    .catch((error) => {
      failureCB('Failed to delete explainer');
    });
}
// ============================================================================
// UNIFIED ORGAN SEGMENTATION UPLOAD FLOW
// ============================================================================
// This function contains all the state management and polling logic to
// bypass the file saving issue with the React components.

export function APIOrganSegUnifiedUploadFlow(file, modality, setUiState, uploadSuccessCB, uploadFailureCB) {

  const triggerUpload = () => {
    APIOrganSegUploadFile(file, modality, uploadSuccessCB, uploadFailureCB);
  };

  if (!file) {
    // If no file is provided, this is just an initial health check.
    APIOrganSegHealthCheck(
      (response) => {
        if (response && (response.status === 'ok' || response.status === 'healthy')) {
          setUiState('ready');
        }
      },
      () => {
        setUiState('offline');
      }
    );
    return;
  }

  // This is the main upload flow
  setUiState('uploading'); // Set state to uploading immediately

  APIOrganSegHealthCheck(
    (response) => {
      // The backend can return 'ok' (from proxy) or 'healthy' (from EC2)
      if (response && (response.status === 'ok' || response.status === 'healthy')) {
        triggerUpload();
      } else {
        // Server is starting or offline - user must manually start it first
            setUiState('offline');
        uploadFailureCB('Server is not online. Please click the "Start" button above and wait for the server to come online before uploading.');
      }
    },
    (error) => {
      // Server is offline - user must manually start it first
          setUiState('offline');
      uploadFailureCB('Server is offline. Please click the "Start" button above and wait for the server to come online before uploading.');
    }
  );
}

export async function APIExternalLogin(token, provider, successCB, failureCB) {
  const url = `${APIEndpoint}/login/external`;
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({ token: token, provider: provider }),
  })
  .then(response => { 
    if (!response.ok) {
      throw new Error(`Server responded with ${response.status} when requesting upload URL.`);
    }
    return parseJson(response);
  })
  .then(data => {
    successCB(data);
  })
  .catch(failureCB);
}

export function APIGetExplainerData(explainerId, successCB, failureCB) {
  const url = `${APIEndpoint}/explainer/${explainerId}`;
  return safeFetch(url, {
    method: "GET",
    credentials: "include",
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body || 'Failed to get explainer data');
      }
    })
    .catch((error) => {
      failureCB(error);
    });
}

export function APIEditExplainer(explainerId, data, successCB, failureCB) {
  const url = `${APIEndpoint}/explainer/${explainerId}`;
  return safeFetch(url, {
    method: "PUT",
    credentials: "include",
    body: JSON.stringify(data),
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body || 'Failed to edit explainer');
      }
    })
    .catch((error) => {
      failureCB(error);
    });
}

export function APITranslate(texts, language, successCB, failureCB) {
  const url = `${APIEndpoint}/translate`;
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({ texts: texts, language: language }),
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        successCB(body);
      } else {
        failureCB(body || 'Failed to translate');
      }
    })
    .catch((error) => {
      failureCB(error);
    });
}

export function APICreateMtlCopy(visId, language, isOrg, landmarkAudioAction, dubbingVoice, successCB, failureCB, message) {
  const url = `${APIEndpoint}/visualizations/translate`;
  const id = softRandomUUID();
  signalQueryStart(id, message || "Creating translated copy of visual");
  const successWrapper = (vis) => {
    signalQuerySuccess(id, vis.visualization_url);
    successCB(vis);
  };
  const failureWrapper = (err) => {
    if(typeof err === 'string') {
      err = err.replaceAll('"', "");
    }
    signalQueryFailure(id, err.message || (typeof err === 'string' ? err : 'Failed to create translated copy.'));
    failureCB(err);
  };
  return safeFetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify({ id: visId, 
      language: language, 
      landmark_audio_action: landmarkAudioAction, 
      dubbing_voice: dubbingVoice,
      is_org: isOrg }),
  })
    .then(async (response) => {
      const body = await parseJson(response);
      if (response.ok) {
        const newId = body.id;
        const updateId = body.update_id;
        pollForUploadedFile(newId, successWrapper, failureWrapper, 5,
          (body) => {
            if (body.visualization.last_update_id === updateId) {
              return "complete";
            }
            if (body.visualization.last_update_error === updateId) {
              return "error";
            }
          },
          (body) => body.visualization.update_error,
          5
        )
      } else {
        failureWrapper(body || 'Failed to create translated copy');
      }
    })
    .catch((error) => {
      failureWrapper(error);
    }
    );
}